export const personalInfo = {
  name: "Abraham Gogulamudi",
  title: "Senior Engineering Manager",
  tagline: "Technology Strategy | Healthcare Platforms | Cybersecurity & Embedded Systems",
  headline: "Technology Executive | Organization Architect | Open Source & Security Advocate",
  summary: "Accomplished technology leader with 19+ years architecting scalable, secure embedded medical platforms at GE HealthCare. Drives enterprise medical-grade Linux OS platform (Class C, Yocto-based) as foundational enabler for IoMT devices across Surgical, Observation, and Critical Care.",
  detailedSummary: "At GE HealthCare, I lead the architecture and development of the PCS medOS platform — a Class C-compliant, Yocto Project-based operating system powering next-generation patient monitoring and critical-care devices. I excel in technology consolidation, Inner Source practices, and cybersecurity (PKI, SBOM) to deliver engineering efficiency, 40% cost reduction, and regulatory readiness. My passion lies in designing trustworthy systems: from secure boot chains and kernel hardening, to SBOM-driven transparency, defence-in-depth architectures, and lifecycle security aligned with FDA, EU MDR, and CRA regulations. Recent CTO and AI executive education positions me for Director-level impact.",
  photo: "https://customer-assets.emergentagent.com/job_self-brand-hub-1/artifacts/glyl4i0m_2_L4A6376_compressed.jpg",
  contact: {
    phone: "+91 - 9945452856",
    email: "abrahamlincolng@gmail.com",
    location: "Bangalore, India",
    linkedin: "https://www.linkedin.com/in/abraham-gogulamudi-6a398216/",
    medium: "https://medium.com/@abrahamlincolng",
    github: "https://github.com/abrahamlincolng",
    linuxFoundation: "https://openprofile.dev/profile/abrahamlincolng"
  },
  stats: [
    { value: "6+", label: "Products Delivered", description: "End-to-end MedTech from concept to manufacturing" },
    { value: "40%", label: "Cost Reduction", description: "Through medical OS platform consolidation" },
    { value: "15+", label: "Products Enabled", description: "Across 4 foundational platforms at scale" }
  ]
};

export const competencies = [
  {
    title: "Strategic & Technical Vision",
    description: "Platform strategy, architecture, and emerging tech leadership (AI, cloud-edge, IoMT). Drove enterprise OS consolidation delivering 40% cost reduction and enabling 15+ products across 4 foundational platforms.",
    icon: "Target"
  },
  {
    title: "Business & Stakeholder Acumen",
    description: "Cross-functional influence spanning engineering, product, business, and regulatory teams. Risk management and regulatory navigation across FDA, EU MDR, and Cyber Resilience Act frameworks.",
    icon: "BarChart3"
  },
  {
    title: "Delivery Excellence",
    description: "End-to-end delivery of 6+ MedTech products from concept to manufacturing. Cybersecurity, compliance, agile/DevOps, and measurable efficiency gains with ReadyFix fleet management.",
    icon: "Shield"
  },
  {
    title: "Servant Leadership & Team Building",
    description: "Passionate servant leader who inspires high-performing teams through vision and continuous learning. Scaled Scrum teams from 1 to 10, championing Inner Source practices and innovation culture.",
    icon: "Users"
  },
  {
    title: "Cybersecurity & Compliance",
    description: "Expert in medical device cybersecurity (PKI, SBOM, Defence in Depth), FDA pre/post-market guidance, EU MDR, CRA readiness, IEC 62443 standards, and secure SDLC practices.",
    icon: "Lightbulb"
  },
  {
    title: "Innovation & Digital Transformation",
    description: "Driving IoMT, edge computing, and AI/LLM innovation in healthcare. Patent holder for patient interaction methods. CTO Programme and AI Strategies executive education.",
    icon: "Cpu"
  }
];

export const experience = [
  {
    role: "Senior Engineering Manager",
    company: "GE HealthCare, Patient Care Solutions",
    period: "Dec 2022 \u2013 Present",
    location: "Bangalore, India",
    current: true,
    highlights: [
      "Provide strategic leadership for Class C-compliant medical-grade OS platform (PCS medOS) enabling secure IoMT connectivity across Surgical, Observation, and Critical Care",
      "Execute shared OS strategy delivering 40% cost reduction, improved scalability, and engineering efficiency across 15+ products on 4 foundational platforms",
      "Own OS Vitality, ReadyFix fleet management strategy, and cybersecurity posture (SBOM, PKI) \u2014 strengthening device uptime, serviceability, and customer trust",
      "Champion technology consolidation and Inner Source practices to accelerate innovation and connected-care vision",
      "Manage diverse, high-performing team spanning development, design, testing, project management, architecture, and performance optimization",
      "Engage with product and business teams to define and execute platform roadmap across multiple care segments"
    ]
  },
  {
    role: "Staff Software Architect",
    company: "GE HealthCare, Patient Care Solutions",
    period: "Apr 2020 \u2013 Nov 2022",
    location: "Bangalore, India",
    current: false,
    highlights: [
      "Architected Mural Connect Hub \u2014 IoMT edge connectivity platform capturing and harmonizing bedside medical device data for complete patient picture, clinical decision support, and analytics",
      "Designed Mural Connect CloudGateway product architecture integrating Azure IoT Edge Platform on Yocto-based embedded Linux distribution",
      "Enabled Docker container virtualization on embedded device platforms powered by NXP i.MX6 Application Processor",
      "Architected secure Embedded Linux OS distribution based on Yocto Project, supporting 10+ medical devices with robust OS lifecycle management",
      "Supported Carestation Insights LIVE Application, Mural Connect, and Mural Connect CloudGateway products within Patient Care Solutions"
    ]
  },
  {
    role: "Staff Technical Product Manager",
    company: "GE HealthCare, Diagnostic Cardiology",
    period: "Apr 2019 \u2013 Mar 2020",
    location: "Bangalore, India",
    current: false,
    highlights: [
      "Championed the 'create once and reuse everywhere' concept for Diagnostic Cardiology Software and Hardware Platforming definition journey",
      "Strategized and implemented process optimizations including vulnerability management, DevOps architecture for CI/CD infrastructure, and platform PRD",
      "Acquired first patent: Patient Healthcare Interaction Device and Methods (US 20190198169 15/910686)",
      "Drove manufacturing automation and platform standardization across the MAC product family"
    ]
  },
  {
    role: "Certified Product Owner",
    company: "GE HealthCare, Diagnostic Cardiology",
    period: "Apr 2015 \u2013 Mar 2019",
    location: "Bangalore, India",
    current: false,
    highlights: [
      "Delivered first platform-based ECG product (MAC VU360) as Agile Product Owner \u2014 translating product, manufacturing, and service goals into prioritized requirements",
      "Led embedded platform development team to create Common Embedded Linux OS and application platform for multiple products",
      "Developed over-the-air software updates, configuration backup capabilities, and remote serviceability features",
      "Served as Certified Scrum Master and Tech Lead \u2014 played pivotal role in Agile Transformation, scaling Scrum teams from 1 to 10",
      "Streamlined deployment through continuous integration tools and strengthened methodologies with code quality documentation"
    ]
  },
  {
    role: "System Specialist / Senior Design Engineer",
    company: "GE HealthCare, Diagnostic Cardiology",
    period: "May 2010 \u2013 Mar 2015",
    location: "Bangalore, India",
    current: false,
    highlights: [
      "Contributed to successful launch of MAC 2000, MAC 1600, and MAC 800 ECG products \u2014 involved from design inception through entire lifecycle",
      "Spearheaded identification of next-gen hardware (iMX6, Sitara, BeagleBone, Intel Bay Trail) and software (Windows CE, Embedded Linux, Yocto) platforms for multi-product platforming",
      "Developed Windows CE 6.0 BSP for Freescale iMX27-based custom hardware, USB thermal writer, ECG acquisition drivers, and MFC application frameworks",
      "Created innovative software-based emulator that reduced hardware dependency for marketing and application development teams",
      "Hardware and software qualification for next-generation DCAR ECG products"
    ]
  },
  {
    role: "Senior Design Engineer",
    company: "Harman International (Symphony Services)",
    period: "Nov 2008 \u2013 Apr 2010",
    location: "Bangalore, India",
    current: false,
    highlights: [
      "Collaborated with Texas Instruments to provide platform support for TI Application Processor Silicons across Windows CE, Windows Auto, QNX, and VxWorks",
      "Developed MFC-based Media Player, Bluetooth profiles, speech-to-text algorithms, and automotive infotainment solutions",
      "Platform enablement for TI's Davinci and Jacinto processor families"
    ]
  },
  {
    role: "Staff Software Engineer",
    company: "TES Electronic Solutions (Tesslove)",
    period: "Aug 2006 \u2013 Oct 2008",
    location: "Bangalore, India",
    current: false,
    highlights: [
      "Developed software for CMOS sensors and created custom BSP for TI Davinci board",
      "Created WDM device drivers and API libraries for PCIe-based converters",
      "Developed NDIS network drivers for IPTV HD video receivers and ported USB network drivers to KMDF",
      "Built API libraries for peripherals (GSM modems, Bluetooth) on Magellan Promark-4 navigation system across PowerPC and TI Davinci platforms"
    ]
  }
];

export const projects = [
  {
    name: "MAC 2000",
    category: "Diagnostic Cardiology",
    description: "12-channel electrocardiograph with 7\" color display, Marquette 12SL analysis, A4 thermal print, LAN/Wi-Fi/modem connectivity, internal storage for 200 ECGs, and PDF/XML export.",
    overview: "Developed complete software stack including Windows CE 6.0 BSP for Freescale iMX27 hardware, custom USB-based thermal writer driver, ECG acquisition device drivers, MFC-based application frameworks, and eMUX/CETK test automation. Created innovative software emulator reducing hardware dependency.",
    image: "https://www.gehealthcare.in/-/jssmedia/widen/2018/01/25/0204/gehealthcarecom/migrated/2018/01/25/3/0657/ukimages-products-categories-diagnostic-cardiology-mac-2000-mac-2000-spotlight.jpg?h=400&iar=0&w=1200",
    tech: ["Windows CE 6.0/2013", "BSP", "MFC", "C++", "eMUX", "CETK", "Wi-Fi", "IMX27", "Freescale"],
    url: "https://www.gehealthcare.in/products/diagnostic-cardiology/resting-ecgs/mac-2000"
  },
  {
    name: "MAC 1600",
    category: "Diagnostic Cardiology",
    description: "Reliable diagnostic cardiology device providing accurate ECG readings for diverse clinical needs with Marquette 12SL analysis and EMR connectivity.",
    overview: "Contributed to full product lifecycle from design inception through deployment. Developed Windows CE BSP, MFC application frameworks, custom peripheral drivers, and eMUX/CETK test automation suites for medical-grade precision.",
    image: "https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/height:400/https://cdn.gamma.app/fcyj55qf54ybtzi/a475ddf7cb874894bff81d5a6f523561/original/12485-1-ge-mac-1600.jpg",
    tech: ["Windows CE 6.0/2013", "BSP", "MFC", "C++", "eMUX", "CETK", "Wi-Fi"]
  },
  {
    name: "MAC 800",
    category: "Portable ECG",
    description: "Connected, proven, and portable resting ECG with Marquette 12SL analysis, EMR-ready connectivity (LAN/modem/SD), and clinically-proven diagnostic support for outreach programs.",
    overview: "Created innovative software-based emulator reducing hardware dependency for marketing and application teams. Full stack development from BSP to application layer with Windows CE, MFC, and eMUX/CETK test automation.",
    image: "https://www.gehealthcare.com/middle-east/-/jssmedia/widen/2018/01/25/0204/gehealthcarecom/migrated/2018/01/25/3/0657/product-product-categories-diagnostic-ecg-resting-mac-800-gehc-decg-mac_800_overview_jpg.jpg",
    tech: ["Windows CE 6.0/2013", "BSP", "MFC", "C++", "eMUX", "CETK", "Wi-Fi"],
    url: "https://www.gehealthcare.com/middle-east/products/diagnostic-cardiology/resting-ecgs/mac-800"
  },
  {
    name: "MAC VU360",
    category: "Resting ECG Workstation",
    description: "Smart, integrated, and secure all-in-one resting ECG workstation with Smart Acquisition, Hookup Advisor, 12SL analysis, and full MUSE NX workflow integration. Used by 95% of top US cardiology hospitals.",
    overview: "Led platform architecture and embedded software development for this flagship ECG workstation. Developed secure multi-layered data encryption, ReadyFix fleet management integration, and DICOM/HL7 workflow connectivity. Designated by Vizient as Innovative Technology for workflow integration and diagnostic accuracy.",
    image: "https://www.gehealthcare.com/-/jssmedia/gehc/us/images/products/diagnostic-ecg/mac-vu360/redesign/redesign-updates/1/02-feature-hero/dcar_mv360_hook-up-advisor.png?h=506&iar=0&w=800",
    tech: ["Embedded Linux", "Yocto", "BSP", "Linux Kernel", "Qt", "Squish", "IMX6", "Freescale", "TI Wi-link8", "Laird FIPS Wi-Fi", "Secure Design"],
    url: "https://www.gehealthcare.com/products/diagnostic-ecg/resting-ecg/mac-vu360"
  },
  {
    name: "MAC 7",
    category: "Resting ECG",
    description: "Intuitive touchscreen resting ECG system completing tasks in 31% fewer steps. Features Smart Lead detection, Enhanced Hookup Advisor, Marquette 12SL analysis, and multi-format data exchange (DICOM, HL7, PDF, XML).",
    overview: "Contributed to next-gen embedded platform architecture enabling bidirectional MUSE/CardioSoft communication, LDAP authentication, encrypted patient data, and secure network connections. Cybersecurity architecture minimizes attack surface while monitoring security activities.",
    image: "https://www.gehealthcare.com/-/jssmedia/gehc/us/images/products/diagnostic-ecg/mac-7-resting-ecg/redesign/spin/mac7-pdp-hero-mobile.jpg?h=600&iar=0&w=750",
    tech: ["Embedded Linux", "Yocto", "BSP", "Linux Kernel", "Qt", "Squish", "IMX6", "TI Wi-link8", "Secure Design", "Secure Storage"],
    url: "https://www.gehealthcare.com/products/diagnostic-ecg/resting-ecg/mac-7-resting-ecg"
  },
  {
    name: "MAC 5",
    category: "Portable ECG",
    description: "Simple, precise, and secure lightweight resting ECG with 8.9\" touchscreen, Auto-ECG algorithm, Smart Lead technology, and built-in PDF/XML export — designed for high-volume clinical environments.",
    overview: "Developed embedded software enabling Enhanced Hookup Advisor, role-based LDAP authentication, patient data encryption, and secure bidirectional HL7/DICOM connectivity. Platform approach enables MAC 5 and MAC 5 Lite configurations from shared codebase.",
    image: "https://www.gehealthcare.com/-/jssmedia/gehc/us/images/products/diagnostic-ecg/mac-5/redesign/recolored/mac5-pdp-hero-mobile.jpg?h=600&iar=0&w=750",
    tech: ["Embedded Linux", "Yocto", "BSP", "Linux Kernel", "Qt", "Squish", "IMX6", "Laird FIPS Wi-Fi", "Secure Wipe", "Medical Grade Linux"],
    url: "https://www.gehealthcare.com/products/diagnostic-ecg/resting-ecg/mac-5-resting-ecg"
  },
  {
    name: "Carestation 650",
    category: "Anesthesia Delivery",
    description: "Compact, versatile anesthesia delivery system with 15\" touchscreen, CBS600 Compact Breathing System, and intelligent lighting — designed for constrained OR environments.",
    overview: "Led PCS Device OS platform powering this next-gen anesthesia system. Embedded software architecture enables precision ventilation (tidal volumes as low as 5ml), automated Vital Capacity procedures, Pause Gas Flow safety feature, and integrated CARESCAPE respiratory monitoring at 250 samples/second — supporting pediatric to adult care.",
    image: "https://www.gehealthcare.com/-/jssmedia/widen/2018/01/25/0204/gehealthcarecom/migrated/2018/02/13/0542/ct-categories-anesthesia-delivery-carestation-600-new-gehc-carestation-650-brochure-v3_jpg.jpg",
    tech: ["Embedded Linux", "Yocto", "BSP", "Linux Kernel", "Qt", "Squish", "IMX6", "Freescale", "Medical Grade Linux", "Secure Design", "Secure Storage"],
    url: "https://www.gehealthcare.com/products/anesthesia-solutions/carestation-600-series/carestation-650"
  },
  {
    name: "ReadyFix",
    category: "Fleet Management & PKI",
    description: "Cloud-first, secure-by-design fleet management solution enabling real-time device monitoring, remote software updates, and centralized configuration management with PKI-based security architecture.",
    overview: "Architected the secure infrastructure for this IoMT fleet management platform. Built with industry-standard PKI encryption, certificate-based authentication, and comprehensive audit logging. Enables remote diagnosis, OTA security patch deployment, A/B update scheduling, and device configuration rollback — supporting healthcare regulatory compliance across critical care device fleets.",
    image: "https://www.gehealthcare.com/-/jssmedia/gehc/us/images/services/readyfix/feature-hero-remote.jpg?h=1100&iar=0&w=1650",
    tech: ["PKI", "Cloud Security", "OTA Updates", "Fleet Mgmt", "AWS IoT", "Encryption"],
    url: "https://www.gehealthcare.com/services/deviceready/readyfix"
  },
  {
    name: "Mural Virtual Care",
    category: "IoMT & Azure SaaS",
    description: "Virtual care solution integrating data from multiple bedside systems into a single pane of glass — real-time patient views across care areas, hospitals, or entire health systems.",
    overview: "Architected the Mural Connect Hub edge platform and CloudGateway integrating Azure IoT Edge on Yocto-based embedded Linux. Powers digitization of hospital protocols, care pathways, and Early Warning Scores (EWS) via DECISIOInsight — generating actionable clinical insights through HL7/FHIR interoperability for enterprise-wide decision support.",
    image: "https://store-images.s-microsoft.com/image/apps.51420.5fea60e2-b53a-47c8-a5a9-1a5fbde87356.7c6d0ad8-dbd8-44fb-94ec-906dc7cc10ce.e1687965-d7cd-448b-83e8-d1a0f7ebd640",
    tech: ["Azure", "Embedded Linux", "Yocto", "IMX6", "Ser2net", "Docker", "Azure IoT", "HL7/FHIR"],
    url: "https://marketplace.microsoft.com/hi-in/product/saas/gehealthcare.mural_2020"
  }
];

export const education = [
  {
    degree: "Chief Technology Officer Programme",
    institution: "Indian Institute of Management, Kozhikode",
    period: "Sep 2024 \u2013 Sep 2025",
    details: "Technology Strategy, Digital Transformation, Innovation Management"
  },
  {
    degree: "AI Strategies for Business Transformation",
    institution: "Northwestern University - Kellogg School of Management",
    period: "July 2025",
    details: "Artificial Intelligence, Strategy"
  },
  {
    degree: "B.Tech Electronics & Instrumentation",
    institution: "Bapatla Engineering College",
    period: "2002 \u2013 2006",
    details: "Electronics and Instrumentation Engineering"
  },
  {
    degree: "Diploma in Real Time Operating Systems",
    institution: "Cranes Varsity",
    period: "2006 \u2013 2007",
    details: "RTOS fundamentals and embedded systems development"
  }
];

export const certifications = [
  "Certified Scrum Product Owner (CSPO) \u2013 Scrum Alliance",
  "Certified Scrum Master (CSM) \u2013 Scrum Alliance",
  "Generative AI and LLMOps",
  "RAG Fine-Tuning Advanced Techniques",
  "Introduction to Artificial Intelligence"
];

export const skills = {
  "Embedded & OS": ["Yocto Project", "Embedded Linux", "RTOS", "FreeRTOS", "VxWorks", "QNX", "Windows CE", "Windows IoT", "BSP Development"],
  "Programming": ["C", "C++", "Python", "Shell Scripting", "Qt", "QML", "MFC", "PowerShell", "Assembly"],
  "IoT & Cloud": ["Azure IoT Edge", "Docker", "IoMT", "MQTT", "Edge Computing", "Fleet Management", "OTA Updates"],
  "Architecture": ["NXP i.MX6/8/93/95", "TI Davinci/Sitara", "Intel x86/x64", "ARM", "Qualcomm Snapdragon"],
  "DevOps & Tools": ["Jenkins", "Git", "GitLab", "CI/CD", "Coverity", "Sonar", "Docker", "Grafana", "Squish Test Automation"],
  "Cybersecurity": ["Secure Boot", "SAST/DAST", "Threat Modelling", "Vulnerability Mgmt", "IEC 62443", "FDA Compliance", "PenTest"],
  "Medical Standards": ["HL7", "FHIR", "IHE", "DICOM", "HIPAA", "MDR", "FDA Regulations"],
  "Leadership": ["Agile/SAFe", "Scrum", "Portfolio Management", "Vendor Management", "NPI", "Stakeholder Mgmt"]
};

export const proudOf = [
  "Ability to understand, analyze, and solve problems for customer/business/operations",
  "Continuous and quick learning ability and putting things into action",
  "Courage and tenacity to convey thoughts at all levels to get things done",
  "Inspiring teams with vision/strategy and bringing teams together"
];

export const achievements = [
  { value: "6+", label: "MedTech Products", description: "End-to-end from concept to manufacturing & service (MAC 2000, VU360, MAC 7, MAC 5, Mural Connect, Carestation)" },
  { value: "40%", label: "Cost Reduction", description: "Led secure medical OS platform strategy reducing costs and improving scalability" },
  { value: "4", label: "Foundational Platforms", description: "Scaled platforms enabling 15+ products with enhanced serviceability" },
  { value: "1", label: "Patent Awarded", description: "Patient Healthcare Interaction Device & Methods (US 20190198169)" }
];

export const industryExpertise = [
  { domain: "Healthcare / MedTech", level: "Native", percentage: 95 },
  { domain: "IoMT & Edge to Cloud", level: "Native", percentage: 95 },
  { domain: "Embedded Systems", level: "Native", percentage: 95 },
  { domain: "Open-Source Software", level: "Native", percentage: 92 },
  { domain: "Semiconductor", level: "Native", percentage: 90 },
  { domain: "Regulatory & Compliance", level: "Proficient", percentage: 85 },
  { domain: "Automotive", level: "Intermediate", percentage: 60 }
];

export const patent = {
  title: "Patient Healthcare Interaction Device and Methods",
  number: "US 20190198169 15/910686",
  description: "Innovative methods for patient healthcare interaction that improve clinical workflows and patient outcomes"
};

export const publications = [
  {
    title: "The Critical Role of SBOMs in Medical Device Regulations",
    platform: "Medium",
    date: "March 2026",
    description: "Exploring how Software Bill of Materials (SBOM) standards like SPDX and CycloneDX are becoming essential for IoMT cybersecurity compliance under FDA and EU MDR frameworks.",
    url: "https://medium.com/@abrahamlincolng/the-critical-role-of-sboms-in-medical-device-regulations-0dc112a8fa72",
    image: "https://miro.medium.com/v2/resize:fill:320:214/1*C9D_XevJ7iUMkhldAFpdZQ.png",
    tags: ["SBOM", "FDA", "Medical Devices", "Cybersecurity"]
  }
];

export const linuxFoundation = {
  profileUrl: "https://openprofile.dev/profile/abrahamlincolng",
  profileImage: "https://platform-logos-myprofile-api-prod.s3.us-east-2.amazonaws.com/abrahamlincolng.png?v=1768007272920",
  badges: [
    { name: "LFC101 — Inclusive Speaker Orientation", icon: "Award" },
    { name: "LFC111 — Open Source Technical Documentation", icon: "FileText" }
  ],
  training: [
    { name: "Creating Edge IoT Solutions with EdgeX Foundry", code: "LFEL1003", date: "Aug 2025" },
    { name: "Introduction to Kubernetes on Edge with K3s", code: "LFS156x", date: "Sep 2024" },
    { name: "Open Source Technical Documentation Essentials", code: "LFC111", date: "Jan 2024" },
    { name: "The Inclusive Speaker Orientation", code: "LFC101", date: "Feb 2026" }
  ],
  focusAreas: [
    "Yocto Project ecosystem — Dunfell to Scarthgap modernization",
    "Embedded Linux security — Secure boot, TPM, AppArmor, DM-Verity",
    "Open-source cybersecurity for regulated industries",
    "NXP i.MX platform enablement (i.MX6, i.MX8, i.MX95)",
    "AI/LLM pipelines for embedded security analysis"
  ]
};

export const communityProfiles = {
  medium: { url: "https://medium.com/@abrahamlincolng", label: "Medium" },
  github: { url: "https://github.com/abrahamlincolng", label: "GitHub" },
  linuxFoundation: { url: "https://openprofile.dev/profile/abrahamlincolng", label: "Linux Foundation" },
  linkedin: { url: "https://www.linkedin.com/in/abraham-gogulamudi-6a398216/", label: "LinkedIn" }
};

export const domainExpertise = [
  {
    title: "Cybersecurity & Security Architecture",
    subtitle: "Defence in Depth | SBOM Transparency | CRA & FDA Compliance",
    description: "Architecting comprehensive defence-in-depth security frameworks for connected medical devices and IoMT platforms spanning the full product lifecycle. From hardware root of trust through secure boot chains to application-layer sandboxing, I lead multi-layered security strategies with SBOM-driven transparency (SPDX/CycloneDX), automated CVE intelligence, and secure A/B update frameworks — aligned with FDA pre/post-market guidance, EU MDR, EU Cyber Resilience Act (CRA), and IEC 62443.",
    image: "https://www.gehealthcare.com/-/jssmedia/gehc/us/images/products/diagnostic-ecg/mac-vu360/redesign/redesign-updates/1/02-feature-hero/5-mac-vu360-feature-hero.jpg?h=1100&iar=0&w=1650",
    highlights: [
      "Defence in Depth Architecture for IoMT Devices",
      "SBOM Management — SPDX & CycloneDX Standards",
      "Secure Boot Chain — U-Boot, TPM 2.0, DM-Verity",
      "Kernel Hardening — AppArmor, SELinux, Encrypted Storage",
      "A/B Secure OTA Update Framework (SWUpdate)",
      "Automated CVE Intelligence & Vulnerability Lifecycle",
      "FDA Pre/Post-market & EU MDR Compliance",
      "EU Cyber Resilience Act (CRA) Readiness",
      "STRIDE/DREAD Threat Modelling & Penetration Testing",
      "LLM/RAG Pipelines for Security Artifact Analysis"
    ],
    metrics: [
      { value: "SBOM", label: "Transparency" },
      { value: "DiD", label: "Defence in Depth" },
      { value: "CRA/FDA", label: "Compliance" }
    ],
    tags: ["SBOM", "SPDX", "CycloneDX", "Secure Boot", "TPM 2.0", "AppArmor", "DM-Verity", "Defence in Depth", "CVE Intelligence", "SWUpdate", "Encryption", "IEC 62443", "CRA", "FDA"],
    architectureLayers: [
      { layer: "Application Layer", tech: "AppArmor Sandboxing, Access Control, Audit Logging" },
      { layer: "Operating System", tech: "DM-Verity, Encrypted Storage, SBOM (SPDX/CycloneDX)" },
      { layer: "Boot Chain", tech: "U-Boot Verified Boot, TPM 2.0, Secure Boot" },
      { layer: "Hardware Root of Trust", tech: "HSM, Fuse Configuration, Key Management" }
    ]
  },
  {
    title: "Embedded Systems & MedTech",
    subtitle: "Medical-Grade Linux Platforms at Scale",
    description: "Architected secure, medical-grade embedded Linux OS distributions based on the Yocto Project, supporting 10+ medical devices across Patient Care Solutions. Pioneered the 'build once, deploy many' approach for medical device platforms, ensuring scalability, regulatory compliance, and robust lifecycle management across Freescale/NXP and TI processor architectures.",
    image: "https://www.gehealthcare.com/-/jssmedia/widen/2018/01/25/0204/gehealthcarecom/migrated/2018/01/25/4/0703/product-product-categories-anesthesia-delivery-carestation-600-new-hero-4_jpg.jpg?h=400&iar=0&w=1200",
    highlights: [
      "Yocto-based Medical Linux Distribution",
      "Board Support Package (BSP) Development",
      "NXP i.MX6/8/93/95 & TI Sitara Platforms",
      "Custom Device Drivers (USB, PCIe, SPI, I2C)",
      "OS Lifecycle & Security Patch Management",
      "Multi-product Platform Architecture"
    ],
    metrics: [
      { value: "10+", label: "Medical Devices" },
      { value: "5+", label: "Architectures" },
      { value: "19yr", label: "Embedded" }
    ],
    tags: ["Yocto", "Embedded Linux", "BSP", "Device Drivers", "ARM", "NXP", "RTOS", "C/C++", "FreeRTOS"]
  },
  {
    title: "IoMT & Edge Computing",
    subtitle: "From Sensors to Clinical Insights",
    description: "Designed and architected IoMT (Internet of Medical Things) edge connectivity platforms to harmonize data from multiple bedside medical devices, transforming raw sensor data into actionable clinical insights. Championed the complete 'sensors to insights' strategy covering data acquisition, real-time processing, harmonization, and analytics pipelines using interoperability standards.",
    image: "https://store-images.s-microsoft.com/image/apps.51420.5fea60e2-b53a-47c8-a5a9-1a5fbde87356.7c6d0ad8-dbd8-44fb-94ec-906dc7cc10ce.e1687965-d7cd-448b-83e8-d1a0f7ebd640",
    highlights: [
      "Azure IoT Edge Platform Integration",
      "Docker Containerization on Embedded Devices",
      "HL7, FHIR, IHE, DICOM Interoperability",
      "Fleet Management & OTA Updates",
      "Real-time Data Harmonization",
      "Clinical Decision Support Analytics"
    ],
    metrics: [
      { value: "Azure", label: "Cloud Platform" },
      { value: "Docker", label: "Virtualization" },
      { value: "FHIR", label: "Interop" }
    ],
    tags: ["Azure IoT Edge", "Docker", "MQTT", "HL7", "FHIR", "Edge Computing", "OTA", "Fleet Mgmt"]
  }
];
